import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { GooSpinner } from 'react-spinners-kit';
import { AxiosError } from 'axios';
import { FiCheckCircle } from 'react-icons/fi';
import { CgDice3 } from 'react-icons/cg';

import { useBroadcastQuery, useQuizQuery } from '@hooks';
import { api } from '@services';

import * as S from './styles';
import { BaseInteractionProps } from '../../types';

type MutationFnVariables = Record<'quizId' | 'answerId', number>;

const FEEDBACK_DELAY = 2000;

const Quiz = ({ active: isActive }: BaseInteractionProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: room } = useBroadcastQuery({
    select: ({ id, settings }) => ({
      id,
      isQuizEnabled: !!settings.data.quiz,
    }),
    notifyOnChangeProps: ['data'],
  });

  const quizQuery = useQuizQuery(room?.id);
  const refetchQuiz = quizQuery.refetch;

  const [currentAnswer, setCurrentAnswer] =
    useState<{ quizId: number; answerId: number } | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const showAnswerRef = useRef(false);

  const { mutateAsync, isLoading: isMutating } = useMutation(
    ({ quizId, answerId }: MutationFnVariables) =>
      api.post(`/rooms/${room?.id}/quiz`, {
        quiz_id: quizId,
        answer: answerId,
      }),
    {
      onMutate: ({ quizId, answerId }: MutationFnVariables) => {
        setCurrentAnswer({ quizId, answerId });
        setIsLocked(true);
      },
      onSuccess: ({ data }) => {
        toast.success(data.message);
      },
      onError: (err) => {
        toast.error(
          (err as AxiosError).response?.data.message ||
            t('pages.broadcast.fail'),
        );
        setCurrentAnswer(null);
        setIsLocked(false);
      },
      onSettled: () => {
        const delay = showAnswerRef.current ? FEEDBACK_DELAY : 0;

        setTimeout(async () => {
          setCurrentAnswer(null);
          setIsLocked(false);
          await queryClient.invalidateQueries(['quiz', room?.id]);
        }, delay);
      },
    },
  );

  const hasNoQuizzes =
    !quizQuery.data?.quizzes.length && !quizQuery.data?.totalAnsweredQuizzes;

  const hasAllQuizzesBeenAnswered =
    !!quizQuery.data?.totalAnsweredQuizzes && !quizQuery.data?.quizzes.length;

  const handleQuizAnswer = useCallback(
    (quizId: number, answerId: number, showAnswer: boolean) => () => {
      if (isLocked) return;
      showAnswerRef.current = showAnswer;
      mutateAsync({ quizId, answerId });
    },
    [mutateAsync, isLocked],
  );

  useEffect(() => {
    isActive && refetchQuiz();
  }, [isActive, refetchQuiz]);

  if (hasNoQuizzes) {
    return (
      <S.Container $hide={!room?.isQuizEnabled} $disabled={!isActive}>
        <S.MessageContainer>
          <CgDice3 size="3rem" />
          <p>{t('pages.broadcast.quiz.noQuiz')}</p>
        </S.MessageContainer>
      </S.Container>
    );
  }

  return (
    <S.Container $hide={!room?.isQuizEnabled} $disabled={!isActive}>
      {hasAllQuizzesBeenAnswered ? (
        <S.MessageContainer $success>
          <FiCheckCircle size="3rem" />
          <p>{t('pages.broadcast.quiz.answeredAll')}</p>
        </S.MessageContainer>
      ) : (
        <>
          {quizQuery.data?.quizzes.slice(0, 1).map((quiz) => (
            <S.QuizWrapper key={quiz.id}>
              <h3>
                {quiz.asking}

                {isMutating && (
                  <GooSpinner
                    size={1.5}
                    sizeUnit="rem"
                    color={theme.colors.brand.primary.default}
                  />
                )}
              </h3>

              <S.QuizAnswersContainer>
                {quiz.answers.data.map(({ id, answer, correct }, index) => {
                  const isCurrentQuiz = currentAnswer?.quizId === quiz.id;
                  const displayCorrectAnswer =
                    !!quiz.show_answer &&
                    isCurrentQuiz &&
                    (currentAnswer?.answerId === id ||
                      (!!correct && !!currentAnswer));

                  return (
                    <S.QuizAnswer
                      key={id}
                      $correct={!!correct}
                      $displayCorrect={displayCorrectAnswer}
                      $locked={isLocked}
                    >
                      <button
                        type="button"
                        disabled={isLocked}
                        onClick={handleQuizAnswer(
                          quiz.id,
                          id,
                          !!quiz.show_answer,
                        )}
                      >
                        <strong>
                          <span>{index + 1}.</span>
                        </strong>
                        {answer}
                      </button>
                    </S.QuizAnswer>
                  );
                })}
              </S.QuizAnswersContainer>
            </S.QuizWrapper>
          ))}
        </>
      )}
    </S.Container>
  );
};

export default memo(Quiz);
